import { ClientCartDetail } from "common/types/carts";
import { Optional } from "common/utils/types";
import { CartPriceSummary } from "common/types/priceSummary";
import Spinner from "common/components/Spinner";

import CartItemSummary from "client/carts/components/shared/CartItemSummary";
import CartMessages from "client/carts/components/shared/CartMessages";
import FreeShippingProgress from "client/carts/components/shared/FreeShippingProgress";
import PromoCodes from "client/carts/components/PromoCodes";
import PriceSummary from "client/common/components/display/PriceSummary";
import EmployeePricingNote from "client/common/components/content/EmployeePricingNote";
import { FreeShippingProgressSurface } from "client/analytics/types";

import styles from "../styles/CheckoutSummary.module.less";

type CheckoutSummaryPriceSummaryProps = {
  cart: ClientCartDetail;
  tax?: Optional<number>;
};

const CheckoutSummaryPriceSummary = ({ cart, tax }: CheckoutSummaryPriceSummaryProps) => {
  return (
    <PriceSummary.ExternalTax<CartPriceSummary>
      data={{
        ...cart,
        shipping: cart.is_delivery_info_complete ? cart.shipping : null,
      }}
      tax={tax}
      isTaxFinal={false}
      styles={{
        rows: styles.rows,
        row: styles.row,
        subrows: styles.subrows,
        total: styles.total,
        value: styles.value,
      }}
    />
  );
};

type CheckoutSummaryProps = {
  cart: ClientCartDetail;
  size: "s" | "m";
  tax: Optional<number>;
  /** Where this summary renders, so free shipping banner views report the right placement */
  freeShippingSurface: FreeShippingProgressSurface;
};

const CheckoutSummary = ({ cart, size, tax, freeShippingSurface }: CheckoutSummaryProps) => {
  return (
    <div className={styles.summary}>
      <div className={styles.items}>
        {cart.items.map((cartItem) => (
          <CartItemSummary
            size={size}
            cartItem={cartItem}
            key={cartItem.id}
            hasQuantity={true}
            hasPrice={true}
          />
        ))}
      </div>
      <div className={styles.promoCodes}>
        <PromoCodes cart={cart} />
      </div>
      <FreeShippingProgress
        cart={cart}
        surface={freeShippingSurface}
        className={styles.freeShipping}
      />
      <div className={styles.values}>
        <CheckoutSummaryPriceSummary tax={tax} cart={cart} />
        {cart.should_show_employee_pricing_note && (
          <EmployeePricingNote savings={cart.employee_savings} />
        )}
      </div>
    </div>
  );
};

type CheckoutSiderProps = {
  cart: ClientCartDetail;
  isLoading: boolean;
  tax?: number;
};

/**
 * Sider that renders when there's room only on desktop
 */
const CheckoutSummarySider = ({ cart, isLoading, tax }: CheckoutSiderProps) => {
  return (
    <div className={styles.sider}>
      <div className={styles.titles}>
        <div className={styles.title}>Order summary</div>
        {isLoading && <Spinner />}
      </div>
      <CartMessages className={styles.cartMessages} cart={cart} />
      <CheckoutSummary
        cart={cart}
        size="m"
        tax={tax}
        freeShippingSurface={FreeShippingProgressSurface.CheckoutSummary}
      />
    </div>
  );
};

export default Object.assign(CheckoutSummary, {
  PriceSummary: CheckoutSummaryPriceSummary,
  Sider: CheckoutSummarySider,
});
